'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/** Matches the debounce `nav-shell.tsx` already uses for the collapsed-rail
 * hover peek (`scheduleHide`/`cancelHide`) — same interaction, same delay. */
const CLOSE_DELAY_MS = 150;

export interface UseHoverIntentResult {
    isOpen: boolean;
    /** Cancels any pending close and opens immediately. */
    open: () => void;
    /** Schedules a close after CLOSE_DELAY_MS, cancelling any earlier one —
     * a fresh `open()` within that window (e.g. the cursor landing on a
     * portaled flyout/tooltip a few pixels past the trigger) cancels it. */
    close: () => void;
    /** For libraries that report open state as a single boolean (e.g. HeroUI
     * `Popover`'s `onOpenChange`) rather than separate enter/leave events. */
    setOpen: (open: boolean) => void;
    /** Spread onto the hover/focus trigger element. */
    handlers: {
        onMouseEnter: () => void;
        onMouseLeave: () => void;
        onFocus: () => void;
        onBlur: () => void;
    };
}

/**
 * A hover-controlled `isOpen` boolean (driving a `Tooltip`'s or `Popover`'s
 * `isOpen` prop directly) that closes on a short delay instead of
 * instantly, so it survives the cursor's own transit into the content it's
 * showing.
 *
 * Without this, a raw `onMouseLeave → setIsOpen(false)` reads correct in
 * isolation but flickers in practice two different ways:
 * - A flyout (`Popover`) is a separate, portaled element with a real gap
 *   between it and its trigger — the cursor crosses that gap on the way in,
 *   firing `onMouseLeave` on the trigger a beat before `onMouseEnter` fires
 *   on the content, closing and reopening the flyout on every hover.
 * - A `Tooltip` positioned close to its own trigger can end up with its
 *   rendered bubble overlapping part of the trigger's own hit area, so the
 *   trigger's `onMouseLeave` fires the instant the tooltip appears under
 *   the cursor, closing it, which un-overlaps the trigger and reopens it —
 *   a tight open/close loop for as long as the cursor sits there.
 *
 * Both are the same shape of bug: the "leave" and the next "enter" are
 * genuinely two separate events with a real gap between them, however
 * small, and nothing was bridging it.
 */
export function useHoverIntent(): UseHoverIntentResult {
    const [isOpen, setIsOpen] = useState(false);
    const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const cancelPendingClose = useCallback(() => {
        if (closeTimeoutRef.current !== null) {
            clearTimeout(closeTimeoutRef.current);
            closeTimeoutRef.current = null;
        }
    }, []);

    useEffect(() => cancelPendingClose, [cancelPendingClose]);

    const open = useCallback(() => {
        cancelPendingClose();
        setIsOpen(true);
    }, [cancelPendingClose]);

    const close = useCallback(() => {
        cancelPendingClose();
        closeTimeoutRef.current = setTimeout(() => setIsOpen(false), CLOSE_DELAY_MS);
    }, [cancelPendingClose]);

    const setOpen = useCallback((next: boolean) => (next ? open() : close()), [open, close]);

    const handlers = useMemo(
        () => ({ onMouseEnter: open, onMouseLeave: close, onFocus: open, onBlur: close }),
        [open, close],
    );

    return { isOpen, open, close, setOpen, handlers };
}
